import type {
  AdapterStatus,
  Airport,
  CalendarCandidate,
  CalendarStatus,
  Program,
  Service,
  SessionUser,
  Trip,
  TripPlan,
  WalletItem,
} from '@shared/types';

async function parse<T>(res: Response | Promise<Response>): Promise<T> {
  const resolved = await res;
  const data = (await resolved.json()) as T & { error?: string };
  if (!resolved.ok) throw new Error(data.error || `Request failed (${resolved.status})`);
  return data;
}

export const api = {
  session: () => parse<{ user: SessionUser | null }>(awaitFetch('/api/auth/session')),
  guest: (displayName?: string) =>
    parse<{ user: SessionUser }>(
      awaitFetch('/api/auth/guest', { method: 'POST', json: { displayName } }),
    ),
  login: (passphrase: string, displayName?: string, adminPassphrase?: string) =>
    parse<{ user: SessionUser }>(
      awaitFetch('/api/auth/login', { method: 'POST', json: { passphrase, displayName, adminPassphrase } }),
    ),
  adminUnlock: (adminPassphrase: string) =>
    parse<{ user: SessionUser }>(awaitFetch('/api/auth/admin', { method: 'POST', json: { adminPassphrase } })),
  logout: () => parse<{ ok: boolean }>(awaitFetch('/api/auth/logout', { method: 'POST' })),
  profile: (body: Record<string, unknown>) =>
    parse<{ user: SessionUser }>(awaitFetch('/api/profile', { method: 'PUT', json: body })),
  wallet: () => parse<{ wallet: WalletItem[]; programs: Program[] }>(awaitFetch('/api/wallet')),
  saveWallet: (body: Record<string, unknown>) =>
    parse<{ wallet: WalletItem[] }>(awaitFetch('/api/wallet', { method: 'PUT', json: body })),
  deleteWallet: (id: string) => parse<{ wallet: WalletItem[] }>(awaitFetch(`/api/wallet/${id}`, { method: 'DELETE' })),
  demoWallet: () => parse<{ wallet: WalletItem[]; notice: string }>(awaitFetch('/api/wallet/demo', { method: 'POST' })),
  airports: () => parse<{ airports: Airport[] }>(awaitFetch('/api/airports')),
  airport: (iata: string) => parse<{ airport: Airport; services: Service[] }>(awaitFetch(`/api/airports/${iata}`)),
  adapters: () => parse<{ adapters: AdapterStatus[]; notice: string }>(awaitFetch('/api/adapters')),
  trips: () => parse<{ trips: Trip[] }>(awaitFetch('/api/trips')),
  createTrip: (body: Record<string, unknown>) =>
    parse<{ trip: Trip; plan: TripPlan }>(awaitFetch('/api/trips', { method: 'POST', json: body })),
  trip: (id: string) => parse<{ trip: Trip; plan: TripPlan }>(awaitFetch(`/api/trips/${id}`)),
  deleteTrip: (id: string) => parse<{ ok: boolean }>(awaitFetch(`/api/trips/${id}`, { method: 'DELETE' })),
  compare: (ids: string[]) =>
    parse<{ services: Service[]; notice: string }>(awaitFetch(`/api/compare?ids=${ids.join(',')}`)),
  adminCatalog: () =>
    parse<{ airports: Airport[]; services: Service[] }>(awaitFetch('/api/admin/catalog')),
  addAirport: (body: Record<string, unknown>) =>
    parse<{ ok: boolean; id: string }>(awaitFetch('/api/admin/airports', { method: 'POST', json: body })),
  addService: (body: Record<string, unknown>) =>
    parse<{ ok: boolean; id: string }>(awaitFetch('/api/admin/services', { method: 'POST', json: body })),
  setAirportStatus: (id: string, status: string) =>
    parse<{ ok: boolean }>(awaitFetch(`/api/admin/airports/${id}/status`, { method: 'POST', json: { status } })),
  setServiceStatus: (id: string, status: string) =>
    parse<{ ok: boolean }>(awaitFetch(`/api/admin/services/${id}/status`, { method: 'POST', json: { status } })),
  calendarStatus: () => parse<CalendarStatus>(awaitFetch('/api/calendar/status')),
  calendarConnect: () => parse<{ url: string }>(awaitFetch('/api/calendar/connect')),
  calendarDisconnect: () => parse<{ ok: boolean }>(awaitFetch('/api/calendar/disconnect', { method: 'POST' })),
  calendarScan: () =>
    parse<{ mode: 'oauth' | 'demo'; notice: string; windowDays: number; candidates: CalendarCandidate[] }>(
      awaitFetch('/api/calendar/scan'),
    ),
  calendarImport: (eventIds: string[]) =>
    parse<{
      imported: number;
      skipped: Array<{ eventId: string; reason: string; tripId?: string }>;
      trips: Array<{ trip: Trip; plan: TripPlan }>;
      notice: string;
    }>(awaitFetch('/api/calendar/import', { method: 'POST', json: { eventIds } })),
};

function awaitFetch(path: string, init: RequestInit & { json?: unknown } = {}) {
  const headers = new Headers(init.headers);
  if (init.json !== undefined) headers.set('Content-Type', 'application/json');
  return fetch(path, {
    ...init,
    headers,
    credentials: 'same-origin',
    body: init.json !== undefined ? JSON.stringify(init.json) : init.body,
  });
}
